<template>
  <el-dialog
    v-model="visible"
    class="tier-dialog"
    width="880px"
    :show-close="false"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    :before-close="onBeforeClose"
    append-to-body
  >
    <template #header>
      <!-- top row: title on the left, draft save on the right -->
      <div class="tier-dialog-head">
        <i class="tier-dialog-close" @click="close"><SvgIcon name="close" /></i>
        <span class="tier-dialog-title">{{ dialog_title }}</span>
        <!-- creating keeps Save up here, editing moves it down beside Back -->
        <Button
          v-if="!is_edit"
          class="tier-dialog-draft"
          :style="all_active ? 'bg' : 'border'"
          :color="all_active ? 'dark-blue' : 'black-90'"
          size="40"
          :border_r="6"
          :is_disabled="is_saving"
          @click="save(save_status)"
        >
          {{ save_text }}
        </Button>
      </div>

      <div class="tier-dialog-bar">
        <div class="tier-steps">
          <div :class="['tier-steps-item', step > 1 && 'is-done', step === 1 && 'is-current']">
            <span class="tier-steps-num">
              <SvgIcon v-if="step > 1" name="yes-2" />
              <template v-else>1</template>
            </span>
            <span class="tier-steps-text">Define your tiers</span>
          </div>
          <span class="tier-steps-line"></span>
          <div :class="['tier-steps-item', step === 2 && 'is-current']">
            <span class="tier-steps-num">2</span>
            <span class="tier-steps-text">Set conditions</span>
          </div>
        </div>
      </div>
    </template>

    <TierDefineStep
      v-if="step === 1"
      :tiers="tiers"
      :is_edit="is_edit"
      :show_errors="show_name_errors"
      @notify="onNotify"
    />
    <TierConditionsStep v-else :tiers="tiers" :is_edit="is_edit" :show_errors="show_condition_errors" />

    <!-- .el-dialog is the positioning parent, so this covers the header buttons too -->
    <Loading class="absolute" v-if="is_saving" />

    <div class="tier-dialog-actions">
      <Button
        v-if="step === 2"
        :style="'border'"
        color="black-90"
        size="40"
        :border_r="6"
        :is_disabled="is_saving"
        @click="step = 1"
      >
        Back
      </Button>
      <Button
        v-if="step === 1"
        :style="is_edit ? 'border' : 'border'"
        :color="is_edit ? 'black-90' : 'black-90'"
        size="40"
        :border_r="6"
        :is_disabled="is_saving"
        @click="next"
      >
        Next
      </Button>
      <Button
        v-else-if="!all_active"
        color="dark-blue"
        size="40"
        :border_r="6"
        :is_disabled="is_saving"
        @click="save(status.PUBLISHED)"
      >
        Publish
      </Button>
      <Button
        v-if="is_edit"
        :style="all_active ? 'bg' : 'border'"
        :color="all_active ? 'dark-blue' : 'black-90'"
        size="40"
        :border_r="6"
        :is_disabled="is_saving"
        @click="save(save_status)"
      >
        {{ save_text }}
      </Button>
    </div>

  </el-dialog>

  <!-- editing an existing set offers to save, a brand new one just confirms the walk away -->
  <TierConfirm
    v-if="is_edit"
    :is_show="is_quit_show"
    title="Save your changes"
    confirm_text="Confirm"
    @cancel="discard"
    @confirm="saveAndQuit"
  />
  <TierConfirm
    v-else
    :is_show="is_quit_show"
    title="Close without publishing changes?"
    confirm_text="Yes"
    @cancel="is_quit_show = false"
    @confirm="discard"
  />

  <TierConfirm
    :is_show="is_publish_show"
    title="Are you sure you want to confirm the changes to the Tier?"
    :lines="publish_lines"
    confirm_text="Confirm"
    @cancel="is_publish_show = false"
    @confirm="confirmPublish"
  />
</template>

<script>
import { ElDialog } from 'element-plus';
import Button from '@/components/ui/Button.vue';
import Loading from '@/components/ui/Loading.vue';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import { useNotification } from '@/components/ui/notification';
import TierDefineStep from './TierDefineStep.vue';
import TierConditionsStep from './TierConditionsStep.vue';
import TierConfirm from './TierConfirm.vue';
import {
  areConditionsValid,
  cloneTier,
  createTier,
  hasBlockedFields,
  hasThresholdIssues,
  isStartingTier,
  MAX_TIERS,
  publishSummary,
  syncRanks,
  tierSignature,
  TIER_STATUS,
} from './tierModel';

export default {
  name: 'TierEditDialog',

  components: { ElDialog, Button, Loading, SvgIcon, TierDefineStep, TierConditionsStep, TierConfirm },

  props: {
    is_show: {
      type: Boolean,
      default: false,
    },
    // pass existing tiers to edit them, leave empty to start a new set
    value: {
      type: Array,
      default: () => [],
    },
    // true while the parent's batch save is in flight
    is_saving: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'save'],

  setup() {
    const { openNotBottom } = useNotification();
    return { openNotBottom };
  },

  data() {
    return {
      step: 1,
      tiers: [],
      status: TIER_STATUS,
      // one flag per step, so failing Next never pre-reddens the conditions step
      show_name_errors: false,
      show_condition_errors: false,
      is_quit_show: false,
      is_publish_show: false,
      // snapshot taken on open, used to tell whether anything is unsaved
      baseline: '',
      // already on the way out. el-dialog echoes one more update:modelValue false once the
      // leave transition ends (see afterLeave in element-plus use-dialog), and without this
      // guard that echo re-opens the unsaved prompt
      is_closing: false,
    };
  },

  computed: {
    // el-dialog needs a writable model, the open state stays owned by the parent
    visible: {
      get() {
        return this.is_show;
      },
      set(val) {
        // only ever the afterLeave echo, which is_closing swallows
        if (!val) this.close();
      },
    },

    is_edit() {
      return this.value.length > 0;
    },

    /**
     * Read off the saved set rather than the working copy, so removing a tier mid-session
     * does not retitle the dialog underneath the user. Matches the list page's button.
     */
    dialog_title() {
      if (!this.is_edit) return 'Create';
      return this.value.length >= MAX_TIERS ? 'Edit tier' : 'Edit and add tier';
    },

    /**
     * Every loaded tier is already live, so there is no draft state left to fall back to:
     * saving here can only mean publishing. The draft endpoint would push live tiers back
     * to draft and hide them from creators, so it is taken off the table entirely.
     */
    all_active() {
      return this.value.length > 0 && this.value.every((tier) => tier.status === TIER_STATUS.PUBLISHED);
    },

    save_status() {
      return this.all_active ? TIER_STATUS.PUBLISHED : TIER_STATUS.DRAFT;
    },

    save_text() {
      return this.all_active ? 'Save' : 'Save As draft';
    },

    // the editor holds both the loaded set and the edited one, so a real diff is possible
    publish_lines() {
      return publishSummary(this.value, syncRanks(this.tiers));
    },
  },

  watch: {
    is_show(val) {
      if (val) this.start();
    },
  },

  methods: {
    start() {
      this.step = 1;
      this.show_name_errors = false;
      this.show_condition_errors = false;
      // a brand new set starts with the default tier only, every other tier comes from "Add tier"
      // the very first tier is the default one, everything else comes from "Add tier"
      this.tiers = syncRanks(this.value.length ? this.value.map(cloneTier) : [createTier(true)]);
      this.is_quit_show = false;
      this.is_closing = false;
      this.baseline = this.signature();
    },

    // reordering counts as a change, so the whole list is joined into one signature
    signature() {
      return this.tiers.map(tierSignature).join('|');
    },

    isDirty() {
      return this.signature() !== this.baseline;
    },

    /**
     * ESC reaches el-dialog first, and left alone it hides itself immediately while only
     * telling us once the leave transition ends. That would strand is_show at true with
     * nothing on screen, so the dialog could not be reopened. Intercepting here keeps the
     * open state owned by the parent: done() is deliberately never called, every close
     * still runs through the same unsaved guard as the X button.
     */
    onBeforeClose() {
      if (this.is_saving) return;
      this.close();
    },

    close() {
      if (this.is_closing) return;
      if (this.isDirty()) {
        this.is_quit_show = true;
        return;
      }
      this.finishClose();
    },

    finishClose() {
      this.is_closing = true;
      this.$emit('close');
    },

    discard() {
      this.is_quit_show = false;
      this.finishClose();
    },

    // if validation fails the editor stays open with the errors marked, rather than dropping the work
    saveAndQuit() {
      this.is_quit_show = false;
      if (!this.validateNames()) {
        this.step = 1;
        return;
      }
      // this prompt was already a confirmation, so publishing here does not ask a second time
      if (this.save_status === TIER_STATUS.PUBLISHED && !this.validateConditions()) return;
      this.emitSave(this.save_status);
    },

    next() {
      if (!this.validateNames()) return;
      this.step = 2;
    },

    onNotify(message) {
      this.openNotBottom(message, 'error');
    },

    // a draft only needs names, publishing needs the conditions to hold up as well
    save(status) {
      // a fully live set is never allowed to reach the draft endpoint
      const target = this.all_active ? TIER_STATUS.PUBLISHED : status;
      if (!this.validateNames()) {
        this.step = 1;
        return;
      }
      if (target !== TIER_STATUS.PUBLISHED) {
        this.emitSave(target);
        return;
      }

      if (!this.validateConditions()) return;
      // publishing is what creators see, so it is confirmed with a summary of the changes
      this.is_publish_show = true;
    },

    confirmPublish() {
      this.is_publish_show = false;
      this.emitSave(TIER_STATUS.PUBLISHED);
    },

    emitSave(status) {
      // the parent closes this dialog once the batch lands. syncing the baseline keeps that
      // close from asking about unsaved work, while a failed batch leaves the editor usable.
      const payload = syncRanks(this.tiers).map((tier) => ({ ...cloneTier(tier), status }));
      this.baseline = this.signature();
      this.$emit('save', payload);
    },

    // the flags are only ever switched on, so each field validates live from its first failure
    validateNames() {
      const unnamed = this.tiers.some((tier) => !tier.name.trim());
      if (unnamed) {
        this.show_name_errors = true;
        this.openNotBottom('Every tier needs a name before continuing.', 'error');
        return false;
      }
      return true;
    },

    validateConditions() {
      const incomplete = this.tiers.some((tier) => !isStartingTier(tier) && !areConditionsValid(tier));
      if (incomplete) {
        this.show_condition_errors = true;
        this.step = 2;
        this.openNotBottom('Please complete the conditions for every tier.', 'error');
        return false;
      }
      // out of order thresholds already show under their row
      const clashing = this.tiers.some(
        (tier, index) =>
          hasBlockedFields(tier) ||
          hasThresholdIssues(
            tier,
            this.tiers.filter((_, i) => i !== index)
          )
      );
      if (clashing) {
        this.step = 2;
        this.openNotBottom('Please fix the conditions marked in red.', 'error');
        return false;
      }
      return true;
    },
  },

  mounted() {
    if (this.is_show) this.start();
  },
};
</script>
<style lang="scss">
.tier-dialog {
  --el-dialog-margin-top: 5vh;

  padding: 24px;
  max-width: 92vw;
  border-radius: 8px;

  .el-dialog__header {
    padding: 0;
  }

  // the header stays put, a long tier list scrolls inside the dialog instead of growing it
  .el-dialog__body {
    padding: 16px 0 0;
    max-height: calc(90vh - 220px);
    overflow: auto;
  }
}

.tier-dialog-head {
  display: flex;
  align-items: center;
  gap: 16px;

  // pushes Save As draft to the far right of the title row
  .tier-dialog-draft {
    margin-left: auto;
  }

  .tier-dialog-close {
    cursor: pointer;
    color: var(--black-90);
    width: 14px;
    height: 14px;
    margin: 0;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .tier-dialog-title {
    font: var(--font-med-20);
    color: var(--black-90);
  }
}

.tier-dialog-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid var(--black-20);
}

.tier-steps {
  display: flex;
  align-items: center;

  .tier-steps-item {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--black-75);

    &.is-current {
      color: var(--black-90);

      .tier-steps-num {
        background: var(--black-90);
      }
    }

    &.is-done .tier-steps-num {
      background: var(--green);
    }
  }

  .tier-steps-num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--black-20);
    color: var(--white);
    font: var(--font-med-20);

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .tier-steps-text {
    font: var(--font-med-16);
    white-space: nowrap;
  }

  .tier-steps-line {
    width: 120px;
    height: 3px;
    margin: 0 16px;
    background: var(--black-20);
  }
}

.tier-dialog-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 0 0;
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  z-index: 3;

  .ui-button {
    padding: 0 24px !important;

    & + .ui-button {
      min-width: 132px;
      text-align: center;
    }
  }
}
</style>
